import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useConnectionResources } from "../useConnectionResources";
import { analyticsWebsites } from "../../../api/analyticsWebsites";
import { FormModal } from "../../../components/ui/FormModal/FormModal";
import { Button } from "../../../components/ui/Button/Button";
import { analyticsKey, type AnalyticsScope } from "../queryKeys";
import { errorMessage } from "../format";
import { rangeParams } from "../dateRange";
import type { DateRange, Website, SourceOwner } from "../types";
import { ErrorState, Loading } from "./States";
import styles from "../Analytics.module.css";
export default function AddWebsiteDialog({
  scope,
  range,
  onClose,
}: {
  scope: AnalyticsScope;
  range: DateRange;
  onClose: () => void;
}) {
  const navigate = useNavigate(),
    client = useQueryClient();
  const resources = useConnectionResources(scope);
  const [name, setName] = useState(""),
    [url, setUrl] = useState(""),
    [ga4, setGa4] = useState(""),
    [gsc, setGsc] = useState(""),
    [step, setStep] = useState(0);
  const mutation = useMutation({
    mutationFn: () =>
      analyticsWebsites.create({
        name: name.trim(),
        scope_kind: "unverified",
        scope_value: url.trim() || null,
        primary_ga4_property_id: ga4 || null,
        primary_gsc_site_id: gsc || null,
      }),
    onSuccess: (website) => {
      client.invalidateQueries({ queryKey: analyticsKey(scope, "websites") });
      onClose();
      navigate(
        `/analytics/websites/${website.id}/connections?${rangeParams(range)}`,
      );
    },
  });
  const bound = (source: "ga4" | "gsc", id: string) =>
    resources.websites.data?.items.find(
      (w) =>
        (source === "ga4"
          ? w.primary_ga4_property_id
          : w.primary_gsc_site_id) === id,
    );
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title="Add website"
      submitLabel="Save website"
      isSubmitting={mutation.isPending}
      submitDisabled={!name.trim()}
      onSubmit={() => mutation.mutate()}
      steps={[
        {
          label: "Website",
          content: (
            <div className={styles.formStack}>
              <label>
                Website name
                <input
                  autoFocus
                  maxLength={255}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your website"
                />
              </label>
              <label>
                Website address <span>(optional)</span>
                <input
                  value={url}
                  maxLength={1000}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                />
              </label>
              <p className={styles.caption}>
                Give this website a stable place for its reports. You can
                connect one source, both sources, or add them later.
              </p>
            </div>
          ),
        },
        {
          label: "Sources",
          content: (
            <div className={styles.formStack}>
              <p>
                Choose accessible resources. Similar names do not prove two
                sources cover the same website.
              </p>
              {resources.ga4.isPending || resources.gsc.isPending ? (
                <Loading label="Loading accessible sources…" />
              ) : (
                <>
                  <label>
                    Google Analytics
                    <select
                      value={ga4}
                      onChange={(e) => setGa4(e.target.value)}
                    >
                      <option value="">Connect later</option>
                      {resources.ga4.data?.properties.map((p) => (
                        <option
                          key={p.id}
                          value={p.id}
                          disabled={!!bound("ga4", p.id)}
                        >
                          {p.property_name} · {p.property_id}
                          {bound("ga4", p.id)
                            ? ` (used by ${bound("ga4", p.id)!.name})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Search Console
                    <select
                      value={gsc}
                      onChange={(e) => setGsc(e.target.value)}
                    >
                      <option value="">Connect later</option>
                      {resources.gsc.data?.sites.map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                          disabled={!!bound("gsc", s.id)}
                        >
                          {s.site_url}
                          {bound("gsc", s.id)
                            ? ` (used by ${bound("gsc", s.id)!.name})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              {resources.ga4.isError && (
                <ErrorState
                  error={resources.ga4.error}
                  retry={() => resources.ga4.refetch()}
                />
              )}
              {resources.gsc.isError && (
                <ErrorState
                  error={resources.gsc.error}
                  retry={() => resources.gsc.refetch()}
                />
              )}
              <p className={styles.caption}>
                Sources already saved to another website can be moved from
                Connections after you create this website.
              </p>
            </div>
          ),
        },
        {
          label: "Review",
          content: (
            <div className={styles.formStack}>
              <h3>{name}</h3>
              <p>{url || "Website scope not yet verified"}</p>
              <dl className={styles.review}>
                <dt>Google Analytics</dt>
                <dd>
                  {resources.ga4.data?.properties.find((p) => p.id === ga4)
                    ?.property_name || "Not connected"}
                </dd>
                <dt>Search Console</dt>
                <dd>
                  {resources.gsc.data?.sites.find((s) => s.id === gsc)
                    ?.site_url || "Not connected"}
                </dd>
              </dl>
              <p>
                Saving these resources does not verify their reporting scopes
                are equivalent. Reports will keep source evidence separate.
              </p>
              <p className={styles.caption}>
                After saving, use Refresh data to retrieve the selected period.
              </p>
              {mutation.isError && (
                <p role="alert">{errorMessage(mutation.error)}</p>
              )}
            </div>
          ),
        },
      ]}
      currentStep={step}
      onStepChange={setStep}
      onStepValidate={() => step !== 0 || !!name.trim()}
    >
      <span />
    </FormModal>
  );
}
export function BindingDialog({
  scope,
  website,
  source,
  onClose,
  onConnectGoogle,
  connecting = false,
  currentResourceLabel,
  connectError,
}: {
  scope: AnalyticsScope;
  website: Website;
  source: "ga4" | "gsc";
  onClose: () => void;
  onConnectGoogle?: () => void;
  connecting?: boolean;
  currentResourceLabel?: string;
  connectError?: string | null;
}) {
  const resources = useConnectionResources(scope),
    client = useQueryClient();
  const field =
    source === "ga4" ? "primary_ga4_property_id" : "primary_gsc_site_id";
  const [selected, setSelected] = useState(website[field] ?? "");
  const [removing, setRemoving] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const ownership = useQuery({
    queryKey: analyticsKey(scope, "source-owner", source, selected),
    queryFn: ({ signal }) =>
      analyticsWebsites.sourceOwner(source, selected, signal),
    enabled: !!selected && !removing,
    staleTime: 0,
    retry: false,
  });
  useEffect(
    () => setReviewed(false),
    [
      selected,
      removing,
      ownership.data?.website_id,
      ownership.data?.mapping_version,
    ],
  );
  const donor =
    !removing && ownership.data && ownership.data.website_id !== website.id
      ? ownership.data
      : null;
  const ownershipPending =
    !removing && !!selected && (ownership.isPending || ownership.isFetching);
  const ownershipBlocked =
    !removing && !!selected && (ownershipPending || ownership.isError);
  const canSave =
    (removing || (!!selected && selected !== (website[field] ?? ""))) &&
    (!(removing || donor) || reviewed) &&
    !ownershipBlocked;
  const mutation = useMutation({
    mutationFn: (snapshot: { selected: string; donor: SourceOwner | null }) =>
      snapshot.donor
        ? analyticsWebsites.pair(website.id, {
            source,
            source_id: snapshot.selected,
            expected_mapping_version: website.mapping_version,
            expected_source_website_mapping_version:
              snapshot.donor.mapping_version,
          })
        : analyticsWebsites.update(website.id, {
            expected_mapping_version: website.mapping_version,
            [field]: snapshot.selected || null,
          }),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: analyticsKey(scope, "website", website.id),
      });
      client.invalidateQueries({ queryKey: analyticsKey(scope, "websites") });
      client.invalidateQueries({
        queryKey: analyticsKey(scope, "context", website.id),
      });
      client.invalidateQueries({
        queryKey: analyticsKey(scope, "report", website.id),
      });
      onClose();
    },
  });
  const options =
    source === "ga4"
      ? resources.ga4.data?.properties.map((p) => ({
          id: p.id,
          label: `${p.property_name} · Property ${p.property_id}`,
        }))
      : resources.gsc.data?.sites.map((s) => ({ id: s.id, label: s.site_url }));
  const currentId = website[field];
  const currentLabel =
    options?.find((o) => o.id === currentId)?.label ||
    currentResourceLabel ||
    "Currently linked source";
  const resourceQuery = source === "ga4" ? resources.ga4 : resources.gsc;
  const sourceName = source === "ga4" ? "Google Analytics" : "Search Console";
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={`${removing ? "Disconnect" : currentId ? "Manage" : "Link"} ${sourceName}`}
      submitLabel={
        removing
          ? "Disconnect source"
          : currentId
            ? "Save changes"
            : source === "ga4"
              ? "Link property"
              : "Link site"
      }
      submitVariant={removing ? "error" : "primary"}
      isSubmitting={mutation.isPending}
      submitDisabled={!canSave}
      onSubmit={() => {
        if (canSave)
          mutation.mutate({ selected: removing ? "" : selected, donor });
      }}
    >
      <div className={styles.formStack}>
        {currentId && !removing && (
          <p>
            <strong>Already linked to this website:</strong> {currentLabel}.
            Choose another source only to change it.
          </p>
        )}
        {!currentId && (
          <p>
            Choose the{" "}
            {source === "ga4"
              ? "GA4 property for traffic and engagement"
              : "Search Console site for search performance"}
            . Your Google account may already have access; linking a source adds
            its reports to this website.
          </p>
        )}
        {!removing && (
          <label>
            {source === "ga4"
              ? "Google Analytics property"
              : "Search Console site"}
            <select
              value={selected}
              disabled={
                mutation.isPending ||
                resourceQuery.isPending ||
                resourceQuery.isError
              }
              onChange={(e) => {
                setSelected(e.target.value);
                setReviewed(false);
              }}
            >
              <option value="" disabled>
                Choose a {source === "ga4" ? "property" : "site"}…
              </option>
              {currentId && !options?.some((o) => o.id === currentId) && (
                <option value={currentId}>{currentLabel} (current)</option>
              )}
              {options?.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                  {o.id === currentId ? " (current)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        {(source === "ga4" ? resources.ga4 : resources.gsc).isPending && (
          <Loading label="Loading resources…" />
        )}
        {(source === "ga4" ? resources.ga4 : resources.gsc).isError && (
          <ErrorState
            error={(source === "ga4" ? resources.ga4 : resources.gsc).error}
          />
        )}
        {ownershipPending && (
          <Loading label="Checking this resource’s website…" />
        )}
        {!!selected && ownership.isError && (
          <ErrorState
            error={ownership.error}
            retry={() => ownership.refetch()}
          />
        )}
        {removing ? (
          <p>
            This stops showing {sourceName} reports on {website.name}. Your
            Google account and saved source data are kept.
          </p>
        ) : donor ? (
          <div className={styles.notice}>
            <p>
              This source is already listed as <strong>{donor.name}</strong>.
              Linking it here combines its reports with{" "}
              <strong>{website.name}</strong>. Existing links will open the
              combined website.
            </p>
          </div>
        ) : null}
        {(removing || donor) && (
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              disabled={ownershipBlocked || mutation.isPending}
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
            />
            {removing
              ? `Disconnect ${sourceName} from this website`
              : `Use this source for ${website.name}`}
          </label>
        )}
        {!removing && onConnectGoogle && (
          <div>
            <p>
              Can’t find your {source === "ga4" ? "property" : "site"}? Check
              that your connected Google account has access, then review its
              Google permissions.
            </p>
            <Button
              variant="secondary"
              loading={connecting}
              onClick={onConnectGoogle}
            >
              Review Google access
            </Button>
            {connectError && <p role="alert">{connectError}</p>}
          </div>
        )}
        {currentId && (
          <Button
            variant="ghost"
            disabled={mutation.isPending}
            onClick={() => setRemoving(!removing)}
          >
            {removing ? "Keep this connection" : "Disconnect from this website"}
          </Button>
        )}
        {mutation.isError && (
          <p role="alert">
            {errorMessage(mutation.error)}{" "}
            <Button
              variant="ghost"
              onClick={() => {
                client.invalidateQueries({
                  queryKey: analyticsKey(scope, "website", website.id),
                });
                onClose();
              }}
            >
              Reload details
            </Button>
          </p>
        )}
      </div>
    </FormModal>
  );
}
